import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DealroomCandidates } from './candidates/DealroomCandidates.js';
import { FundsGrid } from './FundsGrid.js';
import { useTRPC } from './trpc.js';

/**
 * Two backlogs, not one. A fund that is not tracked is a fund we hold —
 * editable, enrichable, in the same grid — that nobody has chosen to track. A
 * name the Dealroom feed keeps seeing is Discovered: not a fund at all yet, and
 * the question about it is whether to track it.
 */
type Tab = 'pool' | 'dealroom';

const TAB_KEY = 'nemo.candidates.tab';

function readTab(): Tab {
  try {
    return window.localStorage.getItem(TAB_KEY) === 'dealroom' ? 'dealroom' : 'pool';
  } catch {
    // A private window or a cleared store is simply no preference.
    return 'pool';
  }
}

function writeTab(tab: Tab): void {
  try {
    window.localStorage.setItem(TAB_KEY, tab);
  } catch {
    // Nothing here is worth failing a click over.
  }
}

export function DiscoverPage(): React.JSX.Element {
  const trpc = useTRPC();
  const [tab, setTab] = useState<Tab>(() => readTab());

  const untracked = useQuery(trpc.funds.count.queryOptions({ listStatus: 'pool' }));
  // The same status line the enrichment drawer reads: the pending backlog is
  // one number on it, so the tab costs no query of its own.
  const dealroom = useQuery(trpc.dealroom.status.queryOptions());

  const pending = dealroom.data?.candidatesPending;

  function choose(next: Tab): void {
    setTab(next);
    writeTab(next);
  }

  return (
    <div className="page">
      <div className="tabs" role="tablist" aria-label="Which backlog">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'pool'}
          className={tab === 'pool' ? 'tab active' : 'tab'}
          onClick={() => choose('pool')}
        >
          Not tracked{untracked.data === undefined ? '' : ` (${untracked.data})`}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'dealroom'}
          className={tab === 'dealroom' ? 'tab active' : 'tab'}
          onClick={() => choose('dealroom')}
        >
          Discovered{pending === undefined ? '' : ` (${pending})`}
        </button>
      </div>

      {tab === 'pool' ? <FundsGrid scope="pool" /> : <DealroomCandidates />}
    </div>
  );
}
